
import { useCart } from '../../context/CartContext';

export default function CartDrawer() {
  const { 
    items, 
    removeFromCart, 
    updateQuantity, 
    clearCart, 
    totalItems, 
    totalPrice, 
    isCartOpen, 
    setIsCartOpen 
  } = useCart();

  const formatPrice = (price: number) => {
    return price.toString().replace(/\B(?=(\d{3})+(?!\d))/g, '.');
  };

  const handleWhatsAppCheckout = () => {
    const itemsList = items.map(item => 
      `• ${item.name} x${item.quantity} pack(s) - $${formatPrice(item.packPrice * item.quantity)}`
    ).join('%0A');
    
    const message = `¡Hola! Quiero realizar el siguiente pedido:%0A%0A${itemsList}%0A%0A*Total: $${formatPrice(totalPrice)}*`;
    window.open(`https://wa.me/56912345678?text=${message}`, '_blank');
  };

  if (!isCartOpen) return null;

  return (
    <>
      {/* Overlay */}
      <div 
        className="fixed inset-0 bg-black/60 z-50 transition-opacity"
        onClick={() => setIsCartOpen(false)}
      />
      
      {/* Drawer */}
      <div className="fixed top-0 right-0 h-full w-full max-w-md bg-zinc-900 z-50 shadow-2xl transform transition-transform">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-lime-500/20">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-lime-500 rounded-full flex items-center justify-center">
              <i className="ri-shopping-cart-2-line text-black text-xl"></i>
            </div>
            <div>
              <h2 className="text-xl font-bold text-white">Mi Carrito</h2>
              <p className="text-sm text-gray-400">{totalItems} producto(s)</p>
            </div>
          </div>
          <button 
            onClick={() => setIsCartOpen(false)}
            className="w-10 h-10 flex items-center justify-center text-gray-400 hover:text-white transition-colors"
          >
            <i className="ri-close-line text-2xl"></i>
          </button>
        </div>

        {/* Content */}
        <div className="flex flex-col h-[calc(100%-180px)] overflow-hidden">
          {items.length === 0 ? (
            <div className="flex-1 flex flex-col items-center justify-center p-8">
              <div className="w-24 h-24 bg-lime-500/10 rounded-full flex items-center justify-center mb-4">
                <i className="ri-shopping-cart-line text-5xl text-lime-500/50"></i>
              </div>
              <p className="text-gray-400 text-center">Tu carrito está vacío</p>
              <p className="text-gray-500 text-sm text-center mt-2">Agrega productos para comenzar tu pedido</p>
            </div>
          ) : (
            <div className="flex-1 overflow-y-auto p-4 space-y-4">
              {items.map(item => (
                <div key={item.id} className="bg-black/50 border border-lime-500/20 rounded-xl p-4">
                  <div className="flex gap-4">
                    {/* Image */}
                    <div className="w-20 h-20 bg-gray-800 rounded-lg overflow-hidden flex-shrink-0">
                      <img 
                        src={item.image} 
                        alt={item.name} 
                        className="w-full h-full object-contain"
                      />
                    </div>
                    
                    {/* Info */}
                    <div className="flex-1 min-w-0">
                      <h3 className="text-white font-semibold text-sm truncate">{item.name}</h3>
                      <p className="text-gray-400 text-xs mt-1">{item.unitsPerPack} unidades/pack</p>
                      <p className="text-lime-400 font-bold mt-2">${formatPrice(item.packPrice)}/pack</p>
                    </div>
                    
                    {/* Remove */}
                    <button 
                      onClick={() => removeFromCart(item.id)}
                      className="w-8 h-8 flex items-center justify-center text-gray-500 hover:text-red-400 transition-colors"
                    >
                      <i className="ri-delete-bin-line text-lg"></i>
                    </button>
                  </div>
                  
                  {/* Quantity */}
                  <div className="flex items-center justify-between mt-4 pt-4 border-t border-lime-500/10">
                    <div className="flex items-center gap-3">
                      <button 
                        onClick={() => updateQuantity(item.id, item.quantity - 1)}
                        className="w-8 h-8 bg-lime-500/20 hover:bg-lime-500/30 rounded-lg flex items-center justify-center text-lime-400 transition-colors"
                      >
                        <i className="ri-subtract-line"></i>
                      </button>
                      <span className="text-white font-semibold w-8 text-center">{item.quantity}</span>
                      <button 
                        onClick={() => updateQuantity(item.id, item.quantity + 1)}
                        className="w-8 h-8 bg-lime-500/20 hover:bg-lime-500/30 rounded-lg flex items-center justify-center text-lime-400 transition-colors"
                      >
                        <i className="ri-add-line"></i>
                      </button>
                    </div>
                    <p className="text-white font-bold">
                      ${formatPrice(item.packPrice * item.quantity)}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Footer */}
        {items.length > 0 && (
          <div className="absolute bottom-0 left-0 right-0 p-6 bg-zinc-900 border-t border-lime-500/20">
            <div className="flex items-center justify-between mb-4">
              <span className="text-gray-400">Total del pedido:</span>
              <span className="text-2xl font-black text-lime-400">${formatPrice(totalPrice)}</span>
            </div>
            
            <button 
              onClick={handleWhatsAppCheckout}
              className="w-full py-4 bg-lime-500 text-black rounded-xl font-bold text-lg hover:bg-lime-400 transition-colors flex items-center justify-center gap-2 whitespace-nowrap shadow-lg shadow-lime-500/30"
            >
              <i className="ri-whatsapp-line text-2xl"></i>
              Finalizar Pedido
            </button>
            
            <button 
              onClick={clearCart}
              className="w-full mt-3 py-2 text-gray-400 hover:text-red-400 text-sm transition-colors"
            >
              Vaciar carrito
            </button>
          </div>
        )}
      </div>
    </>
  );
}
